<script setup lang="ts">
import 'eonasdan-bootstrap-datetimepicker';

import $ from 'jquery';
import moment from 'moment';
import {
    computed, onBeforeUnmount, onMounted, shallowRef, unref, watch,
} from 'vue';

import { timeformat, type TimeLike } from '#public/commons-global';
import { useGenerateIds, useJqueryEventListener } from '#public_ts/composables';

import type { PickerAppearance, PickerType, ValueFormat } from './bootstrap-datepicker';
import {
    NullTime,
    TypeClasses,
} from './bootstrap-datepicker';

//string | null is not supported in vue 2.7
const props = defineProps<{
    id?: string;
    name?: string;
    stringValue?: string | null;
    momentValue?: Date | null;
    type: PickerType;
    format?: ValueFormat;
    disabled?: boolean;
    appearance?: PickerAppearance;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:stringValue', value: string): void;
    (event: 'update:momentValue', value: Date): void;
}>();
const pickerElement = shallowRef<HTMLElement | undefined>();
let selectedValue: moment.Moment | null = null;
const generateIds = useGenerateIds();
const controlId = props.id || generateIds();
const controlName = (props.name === '--') ? undefined : (props.name || controlId);

const typeClass = computed(() => TypeClasses[props.type]);

function getDatePicker(_pickerElement: HTMLElement) {
    return $(_pickerElement).data('DateTimePicker');
}

//picker time is always local
//in case of date only - timeformat will return UTC based moment
//we need to convert date only moment to local date, otherwise picker will use wrong date
/*
doesn't compile in vue
function normalizeMoment(time: null): null;
function normalizeMoment(time: moment.Moment): moment.Moment;
function normalizeMoment(time: moment.Moment | null): moment.Moment | null;
*/
function normalizeMoment(time: moment.Moment | null) {
    if(!time) {
        return null;
    }

    if(props.type === 'date') {
        //convert to local and reset time
        return moment([time.year(), time.month(), time.date(), 0, 0, 0, 0]);
    }
    return moment(time).local();
}

function valueChanged(force?: boolean) {
    let value: TimeLike | undefined = props.stringValue ?? undefined;
    if(value === undefined) {
        value = props.momentValue ?? undefined;
    }
    if(value === undefined) {
        return;
    }
    if((value === null) || (value === '') || (value === NullTime)) {
        if(!force && (selectedValue === null)) {
            return;
        }
        selectedValue = null;
    } else {
        const _selectedValue = normalizeMoment(new timeformat(value).toMoment())!;
        if(!force && _selectedValue.isSame(selectedValue)) {
            return;
        }
        selectedValue = _selectedValue;
    }
    const _pickerElement = unref(pickerElement);
    if(_pickerElement) {
        getDatePicker(_pickerElement).setValue(selectedValue);
    }
}
function pickerChanged() {
    const _pickerElement = unref(pickerElement);
    if(_pickerElement) {
        const val = getDatePicker(_pickerElement).getDate();
        if((!val && !selectedValue) || (val && selectedValue && selectedValue.isSame(val))) {
            return;
        }
        selectedValue = normalizeMoment(val);
        emit('update:momentValue', selectedValue ? selectedValue.toDate() : NullTime);

        let formattedValue = '';
        if(selectedValue) {
            const options = (props.type === 'date') ? { needTime: false, zone: false } : undefined;
            if(props.format === 'xml') {
                //timeformat will convert to UTC keeping offset (and we have moment in local time)
                //we don't want to break something
                if(props.type === 'date') {
                    formattedValue = new timeformat(moment(selectedValue).utc(true), { forceUtc: true }).toXml(options);
                } else {
                    formattedValue = new timeformat(selectedValue).toXml(options);
                }
            } else {
                formattedValue = new timeformat(selectedValue).toDisplayTime(options);
            }
        }
        emit('update:stringValue', formattedValue);
    }
}
function stateChanged() {
    const _pickerElement = unref(pickerElement);
    if(_pickerElement) {
        const picker = getDatePicker(_pickerElement);
        if(props.disabled) {
            picker.disable();
        } else {
            picker.enable();
        }
    }
}

function init() {
    const _pickerElement = unref(pickerElement);
    if(_pickerElement) {
        $(_pickerElement).datetimepicker({
            language: 'it',
            ...unref(typeClass).options,
        });
        stateChanged();
        valueChanged(true);
    }
}

onMounted(init);
onBeforeUnmount(() => {
    const _pickerElement = unref(pickerElement);
    if(_pickerElement) {
        getDatePicker(_pickerElement).destroy();
    }
});

useJqueryEventListener(pickerElement, 'dp.change auto-changed', pickerChanged);
watch([() => props.stringValue, () => props.momentValue, () => props.type], () => {
    valueChanged();
});
watch([() => props.disabled], stateChanged);
</script>
<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
<template>
    <input
        v-if="props.appearance === 'simple'"
        v-bind="$attrs"
        :id="controlId"
        ref="pickerElement"
        :name="controlName"
        class="form-control bs-date-picker"
        :class="[
            typeClass.clazz,
            (props.format === 'xml') ? 'bs-date-picker-xml' : '',
        ]"
        type="text"
        :data-date-format="typeClass.format"
    >
    <div
        v-else
        ref="pickerElement"
        :class="[
            'input-group',
            'date',
            typeClass.clazz,
            'bs-date-picker',
        ]"
    >
        <span v-if="props.appearance === 'button-start'" class="input-group-addon"><span class="bi bi-calendar3" /></span>
        <input
            v-bind="$attrs"
            :id="controlId"
            :name="controlName"
            class="form-control"
            :class="(props.format === 'xml') ? 'bs-date-picker-xml' : ''"
            type="text"
            :data-date-format="typeClass.format"
        >
        <span v-if="!props.appearance || props.appearance === 'button-end'" class="input-group-addon">
            <span class="bi bi-calendar3" />
        </span>
    </div>
</template>
